import React, { useEffect, useState } from 'react';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { GlassIconButton } from 'dashboard/components/common';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import { emptyForm } from './wizard/emptyForm';
import StepBasics    from './wizard/StepBasics';
import StepContract  from './wizard/StepContract';
import StepChain     from './wizard/StepChain';
import StepRoyalties from './wizard/StepRoyalties';
import StepSeaDrop   from './wizard/StepSeaDrop';

/**
 * Step-wise NFT collection setup.
 *
 * Owns the form state + the step index + the save handler. Each step's
 * UI lives in its own file under `./wizard/`. Step count adapts to the
 * drop strategy: the SeaDrop public-phase step (step 4) only appears
 * for `erc721a` + `seadrop`.
 */
export default function CollectionWizard({ open, onClose, catalog, brandId, editing, onSaved }) {
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        ...emptyForm(),
        ...editing,
        seadrop: {
          ...emptyForm().seadrop,
          ...(editing.seadrop || {}),
          public_stage: { ...emptyForm().seadrop.public_stage, ...(editing.seadrop?.public_stage || {}) },
        },
      });
    } else {
      setForm(emptyForm());
    }
    setStep(0);
  }, [open, editing]);

  const chains = catalog?.chains || [];
  const contractTypes = catalog?.contract_types || [];
  const dropStrategies = catalog?.drop_strategies || [];

  const setField       = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSeaDrop     = (k, v) => setForm((f) => ({ ...f, seadrop: { ...f.seadrop, [k]: v } }));
  const setPublicStage = (k, v) =>
    setForm((f) => ({
      ...f,
      seadrop: { ...f.seadrop, public_stage: { ...f.seadrop.public_stage, [k]: v } },
    }));

  const isSeaDrop = form.contract_type === 'erc721a' && form.drop_strategy === 'seadrop';
  const totalSteps = isSeaDrop ? 5 : 4;

  // Per-step gate for the "Next" button. The final step gates the Save
  // call instead. Last step (no SeaDrop) is royalties (step 3), which
  // requires basis points 0–10000.
  const canNext = (() => {
    if (step === 0) return !!form.name && !!form.symbol;
    if (step === 1) return !!form.contract_type;
    if (step === 2) return !!form.chain_id;
    if (step === 3) return Number(form.royalty_basis_points) >= 0 && Number(form.royalty_basis_points) <= 10000;
    return true;
  })();

  const save = () => {
    setSaving(true);
    const payload = {
      ...form,
      chain_id: Number(form.chain_id),
      royalty_basis_points: Number(form.royalty_basis_points || 0),
      max_supply: Number(form.max_supply || 0),
    };
    const call = editing
      ? API.updateNftCollection({ brand_id: brandId, _id: editing._id, patch: payload })
      : API.createNftCollection({ brand_id: brandId, patch: payload });
    call
      .then(() => {
        toast.success(editing ? 'Collection updated' : 'Collection saved as draft');
        onSaved && onSaved();
        onClose();
      })
      .catch((err) => toast.error(err?.message || 'Failed to save'))
      .finally(() => setSaving(false));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth scroll='paper'>
      <DialogTitle sx={{ pr: '48px' }}>
        {editing ? `Edit "${editing.name}"` : 'New NFT collection'}
        <Typography variant='caption' color='text.secondary' display='block'>
          Step {step + 1} of {totalSteps}
        </Typography>
        <GlassIconButton label='Close' onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }} size='small'>
          <CloseIcon fontSize='small' />
        </GlassIconButton>
      </DialogTitle>
      <DialogContent dividers>
        {step === 0 && <StepBasics form={form} setField={setField} />}
        {step === 1 && (
          <StepContract
            form={form}
            setField={setField}
            contractTypes={contractTypes}
            dropStrategies={dropStrategies}
          />
        )}
        {step === 2 && <StepChain form={form} setField={setField} chains={chains} />}
        {step === 3 && <StepRoyalties form={form} setField={setField} />}
        {step === 4 && isSeaDrop && (
          <StepSeaDrop form={form} setSeaDrop={setSeaDrop} setPublicStage={setPublicStage} />
        )}
      </DialogContent>
      <DialogActions sx={{ p: '14px 20px' }}>
        {step > 0 && <Button onClick={() => setStep((s) => s - 1)}>Back</Button>}
        {step < totalSteps - 1 && (
          <Button variant='contained' onClick={() => setStep((s) => s + 1)} disabled={!canNext}>
            Next
          </Button>
        )}
        {step === totalSteps - 1 && (
          <Button variant='contained' onClick={save} disabled={!canNext || saving}>
            {saving ? 'Saving…' : (editing ? 'Save changes' : 'Save draft')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
